import InputGroupContent from '@/components/elements/input'
import { Box, Flex, Heading } from '@chakra-ui/react'
import Submit from '@/components/elements/submit'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from '@/store/store'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { useState } from 'react'
import Toast from '@/components/elements/toast'
import type { InferType } from 'yup'
import SpinnerLoading from '@/components/elements/spinner'
import {
	customersRefrestUpdate,
	sessionRefrestUpdate,
} from '@/store/features/state'
import CustomersEntity from '@/entities/api/customers/customers'
import { updateCustomerById } from '@/services/api/customers/customers'
import { updateCustomerFn } from '@/store/features/customers'
import { UpdateCustomerSchema } from '@/entities/schemas/customer'
import { useFormErrorToast } from '@/hooks/useFormErrorToast'

type UserFormValues = InferType<typeof UpdateCustomerSchema>

const EditCustomer = ({ close }: { close: () => void }) => {
	const customer = useSelector(
		(state: RootState) => state.customers.selectCustomer
	)
	if (!customer) {
		return null
	}
	const token = useSelector((state: RootState) => state.auth.token)
	const [loading, setLoading] = useState(false)
	const {
		register,
		handleSubmit,
		formState: { errors },
	} = useForm<UserFormValues>({
		resolver: yupResolver(UpdateCustomerSchema),
	})
	useFormErrorToast(errors)
	const dispatch = useDispatch<AppDispatch>()

	const onSubmit = async (dataYup: UserFormValues) => {
		try {
			setLoading(true)
			const data: Partial<CustomersEntity> = {
				name: dataYup.name,
				lastname: dataYup.lastname,
				email: dataYup.email,
				phone: dataYup.phone,
			}

			const response = await updateCustomerById(
				token ?? '',
				customer?._id ?? '',
				data
			)

			if (response?.status === 409) {
				Toast({
					type: 'warning',
					message: 'Ya existe un cliente con ese numero o correo',
				})
				setLoading(false)
				return
			}

			if (response?.status === 400) {
				Toast({
					type: 'warning',
					message: 'Informacion erronea, verifica la informacion',
				})
				setLoading(false)
				return
			}

			if (response?.status === 401) {
				dispatch(sessionRefrestUpdate())
				setLoading(false)
				return
			}

			dispatch(
				updateCustomerFn({
					...customer!,
					...dataYup,
				})
			)
			dispatch(customersRefrestUpdate())
			setLoading(false)
			close()
		} catch (error) {
			console.log(error)
			Toast({ type: 'error', message: 'No se pudo crear el cliente' })
			setLoading(false)
			close()
		}
	}

	return (
		<form onSubmit={handleSubmit(onSubmit)}>
			<Flex w='100%' h='full' flexDirection='column' alignItems='center'>
				<Box
					w='full'
					display='flex'
					justifyContent='start'
					alignItems='center'
					mb='2'
				>
					<Heading
						as='h4'
						fontSize={{ base: 'lg', md: 'xl' }}
						color='black'
						fontWeight='900'
					>
						Informacion general
					</Heading>
				</Box>
				<InputGroupContent
					label='Nombre del cliente'
					placeholder='Ingresa el nombre cliente'
					name='name'
					type='text'
					defaultValue={customer?.name}
					register={register}
					errors={errors}
				/>
				<InputGroupContent
					label='Apellidos del cliente'
					placeholder='Ingresa los apellidos del cliente'
					name='lastname'
					type='text'
					register={register}
					defaultValue={customer?.lastname}
					errors={errors}
				/>
				<InputGroupContent
					label='Correo'
					placeholder='Ingresa el correo'
					type='email'
					name='email'
					register={register}
					defaultValue={customer?.email}
					errors={errors}
				/>
				<InputGroupContent
					label='Numero telefonico'
					placeholder='Ingresa el numero telefonico'
					type='number'
					name='phone'
					register={register}
					defaultValue={customer?.phone}
					errors={errors}
				/>
				<Box pt='4' pb='8'>
					{loading ? (
						<SpinnerLoading color='black' />
					) : (
						<Submit text='Agregar cliente' />
					)}
				</Box>
			</Flex>
		</form>
	)
}

export default EditCustomer
